import React, { useEffect, useState } from "react";
import BackToHome from "../../containers/BackToHome";
import CalendarButton from "../../containers/CalendarButton";
import QRCode from "qrcode";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
} from "firebase/firestore";
import { useParams } from "react-router-dom";
import db from "../../utils/firebase.config";

import "./style.css";

const Asistencia = () => {
  const { invitationId } = useParams();
  const [familiyAlias, setFamiliyAlias] = useState("");

  // Local toggle state — what the user sees / edits
  const [guests, setGuests] = useState([]);

  // Snapshot of what is actually persisted in Firestore — only this drives QR display
  const [savedGuests, setSavedGuests] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [toastMsg, setToastMsg] = useState(null); // null | "qr" | "empty"
  const [hasChanges, setHasChanges] = useState(false);

  const [baseUrl] = useState(window.location.origin);

  // Auto-dismiss the toast after 5 s
  useEffect(() => {
    if (!toastMsg) return;
    const t = setTimeout(() => setToastMsg(null), 5000);
    return () => clearTimeout(t);
  }, [toastMsg]);

  // --- Data loading ---
  const parseGuests = (docs) => {
    const arr = [];
    docs.forEach((snapshot) => {
      const data = { ...snapshot.data(), id: snapshot.id, isWard: false };
      arr.push(data);
      (data.wards || []).forEach((ward) => {
        arr.push({ ...ward, isWard: true, guardianId: snapshot.id });
      });
    });
    return arr;
  };

  const fetchGuestDocs = async (invId) => {
    const ref = collection(db, `invitation/${invId}/guess`);
    const snap = await getDocs(query(ref));
    return snap.empty ? [] : snap.docs;
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [invSnap, guestDocs] = await Promise.all([
          getDoc(doc(db, "invitation", invitationId)),
          fetchGuestDocs(invitationId),
        ]);

        if (cancelled) return;

        if (invSnap.exists()) setFamiliyAlias(invSnap.data().familyAlias);

        const parsed = parseGuests(guestDocs);
        setGuests(parsed);
        // Initialize savedGuests from Firestore so already-confirmed guests
        // show their QR immediately on revisit
        setSavedGuests(parsed.map((g) => ({ ...g })));
      } catch (err) {
        console.error("load error", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [invitationId]);

  // --- Toggle ---
  const updateInvitation = (e) => {
    const name = e.target.value;
    // Spread each guest to avoid mutating state objects in place
    setGuests((prev) =>
      prev.map((g) => (g.name === name ? { ...g, confirmed: !g.confirmed } : g))
    );
    setHasChanges(true);
    setToastMsg(null); // clear toast when user makes new changes
    setSaveError("");
  };

  // --- Confirm ---
  const confirmAssistance = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError("");

    try {
      const guestDocs = await fetchGuestDocs(invitationId);
      if (!guestDocs.length) {
        setSaveError("No se encontraron invitados.");
        return;
      }

      const now = new Date().toISOString();

      await Promise.all(
        guestDocs.map(async (guestDoc) => {
          const guestId = guestDoc.id;
          const guestData = { ...guestDoc.data() };
          let changed = false;

          // Match main guest by Firestore document ID (reliable, name-independent)
          const mainGuest = guests.find((g) => !g.isWard && g.id === guestId);
          if (mainGuest && mainGuest.confirmed !== guestData.confirmed) {
            guestData.confirmed = mainGuest.confirmed;
            changed = true;
          }

          // Match each ward by its numeric id + parent guardianId
          if (guestData.wards?.length) {
            const updatedWards = guestData.wards.map((ward) => {
              const wardLocal = guests.find(
                (g) => g.isWard && g.guardianId === guestId && g.id === ward.id
              );
              if (wardLocal && wardLocal.confirmed !== ward.confirmed) {
                changed = true;
                return { ...ward, confirmed: wardLocal.confirmed };
              }
              return ward;
            });
            if (changed) guestData.wards = updatedWards;
          }

          if (changed) {
            guestData.updatedAt = now;
            await updateDoc(
              doc(db, `invitation/${invitationId}/guess`, guestId),
              guestData
            );
          }
        })
      );

      // Only after all writes succeed, promote local state to savedGuests
      setSavedGuests(guests.map((g) => ({ ...g })));
      setToastMsg(guests.some((g) => g.confirmed) ? "qr" : "empty");
      setHasChanges(false);
    } catch (err) {
      console.error("confirm error", err);
      setSaveError("Error al guardar. Verifica tu conexión e intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  // QR is derived from savedGuests — never from local toggle state
  const confirmedSaved = savedGuests.filter((g) => g.confirmed);
  const showQR = confirmedSaved.length > 0;

  // URL that becomes the QR content (the actual confirmation page)
  const buildQrContent = (invId, data) => {
    if (!invId || !data) return null;
    const path = data.isWard
      ? `${invId}/${data.guardianId}/${data.id}`
      : `${invId}/${data.id}/null`;
    return `${baseUrl}/confirmacionqr/${path}`;
  };

  // External API URL used for the display <img> only
  const getQrImgSrc = (invId, data) => {
    const content = buildQrContent(invId, data);
    if (!content) return null;
    return `http://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(content)}&size=200x200&bgcolor=ffffff`;
  };

  // Download a styled card PNG generated entirely client-side
  const downloadQRCard = async (data) => {
    const qrContent = buildQrContent(invitationId, data);
    if (!qrContent) return;

    // Load specific font variants before drawing
    await Promise.all([
      document.fonts.load("italic 300 18px 'Cormorant Infant'"),
      document.fonts.load("600 36px 'Cormorant Infant'"),
      document.fonts.load("600 24px 'Cormorant Infant'"),
      document.fonts.load("10px 'Didact Gothic'"),
    ]).catch(() => {}); // non-fatal if a variant is missing

    const DPR = 2;
    const W = 520;
    const H = data.table ? 660 : 636;

    const canvas = document.createElement("canvas");
    canvas.width = W * DPR;
    canvas.height = H * DPR;
    const ctx = canvas.getContext("2d");
    ctx.scale(DPR, DPR);

    // Background
    ctx.fillStyle = "#faf7f0";
    ctx.fillRect(0, 0, W, H);

    // Outer border
    ctx.strokeStyle = "rgba(178, 140, 93, 0.40)";
    ctx.lineWidth = 1;
    ctx.strokeRect(16, 16, W - 32, H - 32);
    // Inner border
    ctx.strokeStyle = "rgba(178, 140, 93, 0.15)";
    ctx.strokeRect(24, 24, W - 48, H - 48);

    const cx = W / 2;

    // Monogram
    ctx.fillStyle = "#b28c5d";
    ctx.font = "italic 300 18px 'Cormorant Infant', serif";
    ctx.textAlign = "center";
    ctx.fillText("L & M", cx, 64);

    // Rule
    ctx.fillStyle = "rgba(178, 140, 93, 0.55)";
    ctx.fillRect(cx - 30, 72, 60, 1);

    // Names
    ctx.fillStyle = "#3a2f22";
    ctx.font = "600 36px 'Cormorant Infant', serif";
    ctx.fillText("LUCERO & MIGUEL", cx, 110);

    // Date
    ctx.fillStyle = "#8a7060";
    ctx.font = "10px 'Didact Gothic', sans-serif";
    ctx.fillText("19  ·  SEPTIEMBRE  ·  2026", cx, 132);

    // Rule under date
    ctx.fillStyle = "rgba(178, 140, 93, 0.25)";
    ctx.fillRect(cx - 80, 144, 160, 1);

    // QR code (generated client-side, no CORS)
    const qrSize = 220;
    const qrCanvas = document.createElement("canvas");
    await QRCode.toCanvas(qrCanvas, qrContent, {
      width: qrSize,
      margin: 1,
      color: { dark: "#3a2f22", light: "#faf7f0" },
    });
    const qrX = cx - qrSize / 2;
    const qrY = 158;
    // QR border
    ctx.strokeStyle = "rgba(178, 140, 93, 0.30)";
    ctx.lineWidth = 1;
    ctx.strokeRect(qrX - 8, qrY - 8, qrSize + 16, qrSize + 16);
    ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

    // Rule below QR
    const afterQR = qrY + qrSize + 24;
    ctx.fillStyle = "rgba(178, 140, 93, 0.50)";
    ctx.fillRect(cx - 44, afterQR, 88, 1);

    // Guest name
    ctx.fillStyle = "#3a2f22";
    ctx.font = "600 24px 'Cormorant Infant', serif";
    ctx.fillText(data.name, cx, afterQR + 36);

    // Table
    if (data.table) {
      ctx.fillStyle = "#b28c5d";
      ctx.font = "10px 'Didact Gothic', sans-serif";
      ctx.fillText(`MESA  ·  ${data.table}`, cx, afterQR + 58);
    }

    // Bottom credit
    ctx.fillStyle = "rgba(138, 112, 96, 0.40)";
    ctx.font = "9px 'Didact Gothic', sans-serif";
    ctx.fillText(
      "Lucero & Miguel  ·  19.09.2026  ·  Tlajomulco de Zúñiga, Jal.",
      cx,
      H - 32
    );

    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invitacion-${data.name.toLowerCase().replace(/\s+/g, "-")}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  return (
    <section id="asistencia">
      <div className="asist-overlay" />

      <div className="asist-body" data-aos="fade-in" data-aos-duration="1200">

        {/* Header */}
        <div className="asist-header">
          <p className="asist-label">Con gusto los esperamos</p>
          <div className="asist-rule" />
          <h1 className="asist-names">Lucero &amp; Miguel</h1>
          <p className="asist-date">19 · Septiembre · 2026</p>
          <p className="asist-family">{familiyAlias}</p>
        </div>

        <div className="asist-divider" />

        {/* Steps */}
        <div className="asist-steps">
          <div className="asist-step">
            <span className="asist-step-num">01</span>
            <p className="asist-step-text">
              Activa el switch de quienes asistirán
            </p>
          </div>
          <div className="asist-step-sep" />
          <div className="asist-step">
            <span className="asist-step-num">02</span>
            <p className="asist-step-text">
              Presiona <em>Confirmar</em> y guarda tu código QR
            </p>
          </div>
          <div className="asist-step-sep" />
          <div className="asist-step">
            <span className="asist-step-num">03</span>
            <p className="asist-step-text">
              Agrega la boda a tu calendario
            </p>
          </div>
        </div>

        <div className="asist-divider" />

        {/* Guest list */}
        {loading ? (
          <div className="asist-loading">
            <span className="asist-loading-dot" />
            <span className="asist-loading-dot" />
            <span className="asist-loading-dot" />
          </div>
        ) : (
          <div className="asist-guests">
            {guests.map((data, id) => (
              <div key={id} className="asist-guest-row">
                <span className="asist-guest-name">{data.name}</span>
                <label className="asist-toggle">
                  <input
                    type="checkbox"
                    value={data.name}
                    onChange={updateInvitation}
                    checked={data.confirmed}
                  />
                  <span className="asist-toggle-track" />
                </label>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="asist-actions">
          <button
            className={`asist-btn-confirm${hasChanges ? " asist-btn-confirm--pending" : ""}`}
            type="button"
            onClick={confirmAssistance}
            disabled={saving || loading}
          >
            {saving ? "Guardando..." : "Confirmar asistencia"}
          </button>
          <CalendarButton />
        </div>

        {toastMsg === "qr" && (
          <div className="asist-toast">
            <span className="asist-toast-icon">✓</span>
            <span className="asist-toast-text">
              Confirmación registrada · revisa tu QR
            </span>
          </div>
        )}

        {toastMsg === "empty" && (
          <div className="asist-toast asist-toast--neutral">
            <span className="asist-toast-icon">✓</span>
            <span className="asist-toast-text">
              Guardado · ningún asistente seleccionado
            </span>
          </div>
        )}

        {saveError && (
          <div className="asist-toast asist-toast--error">
            <span className="asist-toast-icon">!</span>
            <span className="asist-toast-text">{saveError}</span>
          </div>
        )}

      </div>

      {/* QR section — only shows guests confirmed AND persisted */}
      {showQR && (
        <div className="asist-qr-section">
          <p className="asist-label">Tus códigos QR de acceso</p>
          <div className="asist-rule" style={{ margin: "0 auto 32px" }} />
          <div className="container-qr">
            {confirmedSaved.map((data, id) => (
              <div className="output-box" key={id}>
                <p className="qr-guest-name">{data.name}</p>
                <img src={getQrImgSrc(invitationId, data)} alt="qr-invitation" />
                {data.table && <p className="qr-table">Mesa · {data.table}</p>}
                <button
                  className="button-qr"
                  type="button"
                  onClick={() => downloadQRCard(data)}
                >
                  Descargar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="asist-footer">
        <p className="asist-footer-text">
          ¿Dudas o problemas?{" "}
          <a
            href="https://wa.me/523314631549/?text=%C2%A1Hola!%20tengo%20un%20problema"
            target="_blank"
            rel="noreferrer"
          >
            <span className="help-url">AYUDA</span>
          </a>
        </p>
        <BackToHome />
      </div>
    </section>
  );
};

export default Asistencia;
